import React, { useState, useEffect, useCallback } from "react";
import axios from '../../api/apiClient';

import {
  CreateTableNew as CreateTable,
  SearchBar,
  Button,
  GeneralMessage,
  useGeneralMessage
} from '../../common/APICommon';
import {SetAuditTrail, SetAuditTrailBulk} from './../Reports/AuditTrailReportEmployeeSetup';
import {
  Input,
} from '../../common/Input';
import { token, domain } from '../Security';
import { LoadingSpinner } from '../../common/Loading';
// icon
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from '@mui/icons-material/Edit';
import { Select } from "../../common/Select";

function DevHobby({ user, isHideSideMenu }) {
  // Use the hook for message management
  const { showMessage, message, isSuccess, showGeneralMessage } = useGeneralMessage();

  var headers = [
    "ID",
    "Title",
    "Description",
    "Idea",
    "DevTest",
    "Status",
    "Actions"
  ]
  var accessors = [
    "id",
    "title",
    "description",
    "idea",
    "devTestTitle",
    "isActive",
    "actions"
  ]

  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingRequest, setLoadingRequest] = useState(false);
  const [data, setData] = useState([]);
  const [selectedData, setSelectedData] = useState(null);

  // Dropdown options
  const [devTestOptions, setDevTestOptions] = useState([]);
  const [dropdownsLoading, setDropdownsLoading] = useState(false);

  // Form state
  const [form, setForm] = useState({
    title: "",
    description: "",
    idea: "",
    devTestId: "",
    isActive: true,
  });

  const resetLocalVariables = () => {
    setForm({
      title: "",
      description: "",
      idea: "",
      devTestId: "",
      isActive: true,
    });
    setSelectedData(null);
  }

  // Retrieve
  const [filterData, setFilterData] = useState([]);
  // Add
  const [showAddForm, setShowAddForm] = useState(false);
  // Update
  const [showEditForm, setShowEditForm] = useState(false);

  const apiUrl = domain + "/api/DevHobby";
  const devTestsUrl = domain + "/api/DevTest";

  // Fetch dropdown data from API
  const fetchDropdowns = useCallback(async () => {
    try {
      setDropdownsLoading(true);
      const response = await axios.get(devTestsUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Filter only active dev tests
      const activeDevTests = response.data.filter(test => test.isActive !== false);

      // Transform dev test data
      const transformDevTestData = (data) => {
        return data.map(item => ({
          value: item.id,
          label: `${item.title}`,
          isActive: item.isActive
        }));
      };
      
      setDevTestOptions(transformDevTestData(activeDevTests));

    } catch (error) {
      console.error('Error fetching dev tests:', error);
    } finally {
      setDropdownsLoading(false);
    }
  }, [devTestsUrl]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      setData(response.data);
      setFilterData(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);

      const errorMessage = error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.message ||
        "Error fetching data";
      showGeneralMessage(errorMessage, false);

    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchDropdowns();
    fetchData();
  }, [fetchDropdowns, fetchData]);

  const handleFilter = (value) => {
    setCurrentPage(1);
    const lowercaseValue = value.toLowerCase();
    
    if (!value) {
      // If input is empty, reset to original list
      setData(filterData);
      return;
    }

    const res = filterData.filter((f) => {
      const match1 = f.title?.toString().toLowerCase().includes(lowercaseValue);
      const match2 = f.description?.toString().toLowerCase().includes(lowercaseValue);
      const match3 = f.idea?.toString().toLowerCase().includes(lowercaseValue);
      const match4 = f.devTestTitle?.toString().toLowerCase().includes(lowercaseValue);
      const match5 = f.id?.toString().startsWith(lowercaseValue);
      return match1 || match2 || match3 || match4 || match5;
    });
    
    setData(res);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Prepare data for API - Idea can be null or empty string
    const apiData = {
      title: form.title || "",
      description: form.description || "",
      idea: form.idea || null,  // Send null if empty
      devTestId: form.devTestId ? parseInt(form.devTestId) : null,
      isActive: form.isActive !== undefined ? form.isActive : true,
    };

    console.log('Add Submitting data:', apiData);

    try {
      setLoadingRequest(true);
      await axios.post(apiUrl, apiData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Audit trail logic
      await SetAuditTrail({
        setupArea: "DevHobby", 
        actionTaken: "insert", 
        field: "Add", 
        newValue: `${apiData.title}`, 
        oldValue: "-", 
        dateSaved: new Date(), 
        savedById: user.id
      });
      
      await fetchData();

      showGeneralMessage("DevHobby created successfully!", true);
      closeAddHandleClick();
    } catch (error) {
      console.error('Error creating data:', error.response || error);

      const errorMessage = error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.message ||
        "Failed to create DevHobby";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const handleUpdate = async (event) => {
    event.preventDefault();

    if (!selectedData?.id) {
      showGeneralMessage("No record selected for update", false);
      return;
    }

    const apiData = {
      title: form.title || "",
      description: form.description || "",
      idea: form.idea || null,  // Send null if empty
      devTestId: form.devTestId ? parseInt(form.devTestId) : null,
      isActive: form.isActive !== undefined ? form.isActive : true,
    };

    console.log('Updating data:', apiData);

    try {
      setLoadingRequest(true);
      await axios.put(`${apiUrl}/${selectedData.id}`, apiData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Audit trail logic
      let auditInfo = [];

      if(apiData.title !== selectedData.title){
        auditInfo.push({
          setupArea: "DevHobby",
          actionTaken: "update",
          field: "Title",
          newValue: `${apiData.title}`,
          oldValue: `${selectedData.title}`,
          dateSaved: new Date(),
          savedById: user.id,
        });
      }

      if(apiData.description !== selectedData.description){
        auditInfo.push({
          setupArea: "DevHobby",
          actionTaken: "update",
          field: "Description",
          newValue: `${apiData.description}`,
          oldValue: `${selectedData.description}`,
          dateSaved: new Date(),
          savedById: user.id,
        });
      }

      if(apiData.idea !== selectedData.idea){
        auditInfo.push({
          setupArea: "DevHobby",
          actionTaken: "update",
          field: "Idea",
          newValue: `${apiData.idea || "(empty)"}`,
          oldValue: `${selectedData.idea || "(empty)"}`,
          dateSaved: new Date(),
          savedById: user.id,
        });
      }

      if(apiData.devTestId !== selectedData.devTestId){
        const selectedDevTest = devTestOptions.find(opt => opt.value === parseInt(apiData.devTestId));
        const oldDevTest = devTestOptions.find(opt => opt.value === parseInt(selectedData.devTestId));
        
        auditInfo.push({
          setupArea: "DevHobby",
          actionTaken: "update",
          field: "DevTest",
          newValue: selectedDevTest ? selectedDevTest.label : apiData.devTestId,
          oldValue: oldDevTest ? oldDevTest.label : selectedData.devTestId,
          dateSaved: new Date(),
          savedById: user.id,
        });
      }

      if(apiData.isActive !== selectedData.isActive){
        auditInfo.push({
          setupArea: "DevHobby",
          actionTaken: "update",
          field: "Is Active?",
          newValue: `${apiData.isActive ? "Active" : "Inactive"}`,
          oldValue: `${selectedData.isActive ? "Active" : "Inactive"}`,
          dateSaved: new Date(),
          savedById: user.id,
        });
      }

      if (auditInfo.length > 0) {
        await SetAuditTrailBulk({data: auditInfo});
      }

      setShowEditForm(false);
      await fetchData();

      showGeneralMessage("DevHobby updated successfully!", true);
      closeEditHandleClick();
    } catch (error) {
      console.error('Error updating data:', error.response || error);

      const errorMessage = error.response?.data?.message ||
        error.response?.data?.errors?.[0]?.message ||
        "Failed to update DevHobby";
      showGeneralMessage(errorMessage, false);
    } finally {
      setLoadingRequest(false);
    }
  };

  const editHandleClick = (data) => {
    setSelectedData(data);

    setForm({
      title: data.title || "",
      description: data.description || "",
      idea: data.idea || "",
      devTestId: data.devTestId?.toString() || "",
      isActive: data.isActive !== undefined ? data.isActive : true,
    });
    openEditHandleClick();
  };

  const openAddHandleClick = () => {
    setShowAddForm(true);
  };

  const closeAddHandleClick = () => {
    setShowAddForm(false);
    resetLocalVariables();
  };

  const openEditHandleClick = () => {
    setShowEditForm(true);
  };

  const closeEditHandleClick = () => {
    setShowEditForm(false);
    resetLocalVariables();
  };

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="rounded-full bg-green-100 px-2 py-1 text-xs text-green-800">Active</span>
    ) : (
      <span className="rounded-full bg-red-100 px-2 py-1 text-xs text-red-800">Inactive</span>
    );
  };

  const actions = [
    {
      name: "Edit",
      tooltip: "edit",
      icon: <EditIcon fontSize="small" />,
      className: "bg-green-500 hover:bg-green-600 flex items-center",
      function: editHandleClick,
      visible: true,
    },
  ];

  return (
    <div>
      <div className={`MainBody row ${isHideSideMenu ? "MainBody2" : ""}`}>
        <div className="LeftSpace"></div>
        <div className={`BodySpace ${isHideSideMenu ? "BodySpace2" : ""}`}>
          <div className="MainteInfo shadow-xl">
            <div className="mainteHeader">
              <div className='sub-title row'>
                <span style={{ padding: "0 5px 0 0", color: "#b0b0b0" }}>{"Maintenance >"} </span>
                <span style={{ borderBottom: "1px solid black" }}>{"Dev Hobbies"}</span>
              </div>
              <h3>Dev Hobbies</h3>
            </div>
            <div className='space-y-4 p-4'>
              <SearchBar handleFilter={handleFilter} />

              <Button icon={<AddIcon />}
                name={"Add Dev Hobby"}
                className={"bg-customGreen hover:bg-customGreenHover border-none cursor-pointer"}
                onClick={openAddHandleClick}
              />
              <CreateTable
                tableName={"Dev Hobbies"}
                headers={headers}
                accessors={accessors}
                data={data}
                actions={actions}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                itemsPerPage={10}
                totalItems={data?.length}
                customRenderers={{
                  isActive: (value) => getStatusBadge(value),
                  idea: (value) => value || <span className="text-gray-400">(empty)</span>
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <AddEditForm
        isOpen={showAddForm}
        onClose={closeAddHandleClick}
        form={form}
        setForm={setForm}
        handleSubmit={handleSubmit}
        modalName={"Add Dev Hobby"}
        mode="add"
        devTestOptions={devTestOptions}
        dropdownsLoading={dropdownsLoading}
        showGeneralMessage={showGeneralMessage}
      />
      <AddEditForm
        isOpen={showEditForm}
        onClose={closeEditHandleClick}
        form={form}
        setForm={setForm}
        handleSubmit={handleUpdate}
        modalName={"Update Dev Hobby"}
        mode="edit"
        devTestOptions={devTestOptions}
        dropdownsLoading={dropdownsLoading}
        showGeneralMessage={showGeneralMessage}
      />

      {/* Use the reusable GeneralMessage component */}
      <GeneralMessage
        form={showMessage}
        message={message}
        isSuccess={isSuccess}
      />

      <LoadingSpinner loading={loadingRequest || loading || dropdownsLoading} />

    </div>
  )
}

function AddEditForm({
  isOpen,
  modalName,
  onClose,
  form,
  setForm,
  handleSubmit,
  mode = "add", // "add", "edit"
  devTestOptions,
  dropdownsLoading,
  showGeneralMessage
}) {
  const [errorState, setErrorState] = useState({
    title: false,
    description: false,
    devTestId: false
    // Note: idea is not in errorState since it's optional
  });

  const submit = (e) => {
    e.preventDefault();
    
    // Validation - Idea is optional, so not included
    const newErrorState = {
      title: !form.title,
      description: !form.description,
      devTestId: !form.devTestId
    };

    setErrorState(newErrorState);
    
    // Check if any error exists
    const hasError = Object.values(newErrorState).some(error => error);
    if (hasError) {
      return;
    }
    
    handleSubmit(e);
    setErrorState({
      title: false,
      description: false,
      devTestId: false
    });
  };

  const handleClose = () => {
    onClose();
    setErrorState({
      title: false,
      description: false,
      devTestId: false
    });
  };

  const handleInputChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isOpen) return null;

  const isEditMode = mode === "edit";

  return (
    <>
      <div
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 z-[10000] flex items-center justify-center overflow-auto bg-black/50 p-3 text-gray-700"
      >
        <div className="relative flex h-auto max-h-[90vh] w-full flex-col rounded-lg bg-gray-50 shadow-xl md:w-[90%] lg:w-[60%] xl:w-[50%]">

          {/* Header */}
          <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-lg border-b border-gray-200 bg-white px-6 py-2 shadow-sm">
            <h2 className="flex items-center space-x-2 text-xl font-semibold text-gray-800">
              <span className="text-xl text-gray-600">{modalName}</span>
            </h2>

            <button
              onClick={handleClose}
              className="border-none bg-transparent p-1 text-gray-400 transition-colors hover:text-gray-700"
              aria-label="Close modal"
            >
              <CloseIcon fontSize="medium" />
            </button>
          </div>

          {/* Body */}
          <div className="flex-grow space-y-4 overflow-y-auto p-6">

            {/* DevHobby Form */}
            <div className="rounded-lg border border-gray-300 bg-white px-4 shadow-sm">
              <div className="border-b border-gray-300 py-1">
                <h3 className="font-medium text-gray-700">DevHobby Information</h3>
              </div>
              <div className="grid grid-cols-1 gap-4 pb-3 pt-3">
                <Input
                  label="Title:"
                  value={form.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Enter title"
                  isRequired={true}
                  error={errorState.title}
                  errorMessage="Please fill up this field"
                />
                
                <Input
                  label="Description:"
                  value={form.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Enter description"
                  isRequired={true}
                  error={errorState.description}
                  errorMessage="Please fill up this field"
                />
                
                <Input
                  label="Idea:"
                  value={form.idea}
                  onChange={(e) => handleInputChange('idea', e.target.value)}
                  placeholder="Enter idea (optional)"
                  isRequired={false}  // Changed to false - optional field
                />
                
                <Select
                  label="DevTest:"
                  value={form.devTestId}
                  onChange={(e) => handleInputChange('devTestId', e.target.value)}
                  options={devTestOptions}
                  placeholder={dropdownsLoading ? "Loading dev tests..." : "Select dev test"}
                  isRequired={true}
                  isDisabled={dropdownsLoading}
                  error={errorState.devTestId}
                  errorMessage="Please select a dev test"
                />
                
                <div className="flex items-center pt-2">
                  <input
                    type="checkbox"
                    checked={form.isActive !== undefined ? form.isActive : true}
                    onChange={(e) => handleInputChange('isActive', e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label className="ml-2 text-sm font-medium text-gray-600">
                    Active
                  </label>
                </div>
              </div>
            </div>

          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-2 rounded-b-lg border-t border-gray-200 bg-white p-5 shadow-sm">
            <button
              onClick={handleClose}
              className="cursor-pointer rounded border-none bg-gray-400 px-4 py-2 text-white transition-colors hover:bg-gray-500"
            >
              Cancel
            </button>

            <button
              onClick={submit}
              className="cursor-pointer rounded border-none bg-blue-500 px-4 py-2 text-white transition-colors hover:bg-blue-600"
              disabled={dropdownsLoading}
            >
              {isEditMode ? "Update" : "Save"}
            </button>
          </div>

        </div>
      </div>
    </>
  );
}

export default DevHobby